import React from "react"
import ReactDOM from "react-dom"
import {
    Link,
    FaIcon,
    LazyImage,
    Field,
    EntityView,
    useQuery,
    useIntersection,
    useUnmounted,
} from "lib"
import { BLOCK_TAGS, MARK_TAGS } from "./htmlTags"

const ImageElement = ({ node, other, info }) => {
    return (
        <div>
            {node.src.map((src, i) => (
                <LazyImage key={i} src={src} alt="" />
            ))}
            {renderChildren(node, other, info)}
        </div>
    )
}
const FieldElement = ({ node, other, info }) => (
    <Field info={{ ...info, value: undefined }} field={`meta.${node.field}`} display="embed" />
)

const useEmbed = id => {
    const q = React.useMemo(
        () =>
            id
                ? {
                      query: { _id: id },
                      collection: "embed",
                  }
                : null,
        [id]
    )
    const [data] = useQuery(q)
    //console.log(id, q, data)
    return data?.[0]
}
const EmbedElement = ({ node, other, info }) => {
    const [style, setStyle] = React.useState({})
    const domRef = React.useRef()
    const embed = useEmbed(node?.id)
    const portal = node?.portal
    React.useEffect(() => {
        if (!portal) return
        const rect = domRef?.current?.getBoundingClientRect()
        //console.log(rect)
        setStyle({ top: rect.top })
    }, [portal])
    const className = `embed ${node?._class ?? ""}`
    const visibility = info.fieldInfo._child_visibility
    if (portal) {
        const container = typeof window !== "undefined" ? document.getElementById(portal) : null
        return (
            <div ref={domRef} className="beacon">
                {typeof window !== "undefined" && container
                    ? ReactDOM.createPortal(
                          <EntityView
                              info={info}
                              entity={embed}
                              display="embed"
                              className={className}
                              visibility={visibility}
                              style={style}
                          />,
                          container
                      )
                    : null}
            </div>
        )
    }
    return (
        <EntityView
            info={info}
            entity={embed}
            display="embed"
            className={className}
            visibility={visibility}
        />
    )
}

const renderChildren = (node, other, info) => {
    return node?.children?.map((node, i) => renderNode(node, i, other, info))
}

const Block = ({ Tag, node, other, info }) => {
    const domRef = React.useRef()
    const visible = React.useRef()
    const unmounted = useUnmounted()

    const onIntersect = React.useCallback(
        entry => {
            if (unmounted.current || visible.current || !entry.isIntersecting) return
            visible.current = true
            domRef.current.classList.add("visible")
        },
        [visible, domRef, unmounted]
    )
    const { type, children, ...attrs } = node
    //console.log(fieldInfo, Renderer)
    useIntersection(info.fieldInfo._child_visibility ? domRef : null, onIntersect)
    return (
        <Tag ref={domRef} {...attrs}>
            {renderChildren(node, other, info)}
        </Tag>
    )
}

const renderText = (node, i = 0) => {
    if (node.text)
        return node.text.split("\n").map((item, i) =>
            i > 0 ? (
                <React.Fragment key={i}>
                    <br />
                    {item}
                </React.Fragment>
            ) : (
                <React.Fragment key={i}>{item}</React.Fragment>
            )
        )
}

const addMark = (Mark, content) => <Mark>{content}</Mark>
const renderLeaf = (node, i) => {
    let ret = renderText(node)
    if (node.inlineStyle) ret = <span style={node.inlineStyle}>{ret}</span>
    ret = Object.keys(MARK_TAGS).reduce(
        (acc, mark) => (node[mark] ? addMark(MARK_TAGS[mark], acc) : acc),
        ret
    )
    return <React.Fragment key={i}>{ret}</React.Fragment>
}

const renderNode = (node, i, other, info) => {
    //console.log(node.type, node)
    if (!node.type) return renderLeaf(node, i)

    if (BLOCK_TAGS[node.type])
        return <Block key={i} Tag={BLOCK_TAGS[node.type]} node={node} other={other} info={info} />
    switch (node.type) {
        case "image":
            return <ImageElement key={i} node={node} other={other} info={info} />
        case "field":
            return <FieldElement key={i} node={node} other={other} info={info} />
        case "embed":
            return <EmbedElement key={i} node={node} other={other} info={info} />
        case "link": {
            const attrs = {}
            if (node.target) attrs["target"] = node.target
            if (node.className) attrs["className"] = node.className
            if (node.iconBefore) attrs["iconBefore"] = node.iconBefore
            if (node.iconAfter) attrs["iconAfter"] = node.iconAfter
            return (
                <Link key={i} to={node.url} {...attrs}>
                    {renderChildren(node, other, info)}
                </Link>
            )
        }
        case "span": {
            const { type, children, ...attrs } = node
            return (
                <span key={i} {...attrs}>
                    {renderChildren(node, other, info)}
                </span>
            )
        }
        case "icon":
            return (
                <span key={i} className="icon">
                    <FaIcon icon={node.icon} />
                </span>
            )
        case "iframe":
            return <iframe key={i} title={node.title ?? ""} {...node.attrs}></iframe>
        default:
            console.log(node.type, node)
            return <Block Tag="p" key={i} node={node} other={other} info={info} />
    }
}

const RawSlate2 = ({ domRef, field, value, info, ...other }) => {
    //console.log("render HTML", field, "in", value, info, other)
    if (!value || !Array.isArray(value)) return null
    return value.map((node, i) => renderNode(node, i, other, info))
}
export default React.memo(RawSlate2)
